import { BreakPointCondition, useBreakpointIf, useConfirm } from '@genial/components';
import { instanceOfErrorListResponse, ErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import React from 'react';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { useSubscriptionActions } from '../../../../../../store/subscription/actions';
import { subscriptionMarketStatusQuery } from '../../../../../../store/subscription/selector';
import {
  SubscriptionHistoryItemProps,
  SubscriptionItemProps,
} from '../../../../../../store/subscription/types';
import {
  defaultErrorMessageSubscription,
  getSubscriptionMarketClosedMessage,
} from '../../../../constants';

import { SubscriptionHistoryListItemDesktop } from './Desktop';
import { SubscriptionHistoryListItemMobile } from './Mobile';
import { SubscriptionHistoryListItemTablet } from './Tablet';

export interface SubscriptionHistoryListItemReceivedProps {
  historyItem: SubscriptionHistoryItemProps;
  subscriptionItem: SubscriptionItemProps;
}

export interface SubscriptionHistoryListItemProps extends SubscriptionHistoryListItemReceivedProps {
  onCancel: () => void;
  isDisabled: boolean;
  disabledMessage?: string;
}

export const SubscriptionHistoryListItem: React.FC<SubscriptionHistoryListItemReceivedProps> = (
  props
) => {
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const { confirm } = useConfirm();

  const { showToast, showToastFromError } = useToastCreator('subscriptionSolicitation');
  const { data, loading } = useRecoilQuery(subscriptionMarketStatusQuery);
  const { cancelSubscriptionRequest } = useSubscriptionActions();

  const handleCancel = () => {
    const confirmDescription = `Está ação irá cancelar a solicitação de ${props.historyItem.requestedQuantity} títulos do cliente\n Deseja continuar mesmo assim?`;

    confirm(
      {
        isMobile: isTablet,
        description: confirmDescription,
        title: 'Atenção',
        icon: 'Warning',
        iconColor: 'feedback.attention.default',
        confirmText: 'Continuar',
        confirmColor: 'feedback.error.default',
      },
      async () =>
        cancelSubscriptionRequest(props.historyItem.subscriptionRequestId)
          .then(() => {
            showToast(
              'success',
              'Cancelamento solicitado com sucesso!',
              'Acompanhe o status do cancelamento.'
            );
          })
          .catch((error) => {
            if (instanceOfErrorListResponse(error as object)) {
              (error as ErrorListResponse).errors?.forEach((errorItem) =>
                showToastFromError(errorItem)
              );
              return;
            }

            showToast(...defaultErrorMessageSubscription);
          })
    );
  };

  const allProps = {
    ...props,
    onCancel: handleCancel,
    isDisabled: !props.historyItem.ableToCancel || loading || !data?.isOpen,
    disabledMessage: getSubscriptionMarketClosedMessage(data?.originalData),
  };

  const responsiveItemTable = useBreakpointValue({
    base: <SubscriptionHistoryListItemMobile {...allProps} />,
    sm: <SubscriptionHistoryListItemTablet {...allProps} />,
    lg: <SubscriptionHistoryListItemDesktop {...allProps} />,
  });

  return <>{responsiveItemTable}</>;
};
